import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dataIt = (d) => (d || '').split('-').reverse().join('/');

// Numerazione delle ricevute: progressivo per anno e per data d'incasso. Prima del 2026 e' un numero interno (i condomini hanno le ricevute
// del precedente amministratore). L'anteprima non scrive nulla; applicando, i vecchi numeri restano salvati.
export const RicevuteNumerazione = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const url = `${API_URL}/api/condominiums/${condoId}/ricevute/rinumera`;
  const [piano, setPiano] = useState(null);
  const [fatto, setFatto] = useState(null);
  const [lavoro, setLavoro] = useState(false);

  const chiama = useCallback(async (dry_run) => {
    const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ dry_run }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Operazione non riuscita');
    return j;
  }, [url, token]);

  const anteprima = async () => {
    setLavoro(true); setFatto(null);
    try { setPiano(await chiama(true)); } catch (e) { toast.error(e.message); }
    setLavoro(false);
  };
  const applica = async () => {
    if (!window.confirm('Applicare la nuova numerazione a tutte le ricevute? I numeri attuali restano salvati e le ricevute già aperte si rigenerano con il numero nuovo.')) return;
    setLavoro(true);
    try { const r = await chiama(false); setFatto(r); setPiano(null); toast.success(`${r.cambiate} ricevute rinumerate`); } catch (e) { toast.error(e.message); }
    setLavoro(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Numerazione delle ricevute" description="Progressivo per anno e per data d'incasso. Prima del 2026 è un numero interno." />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Come funziona</CardTitle>
          <CardDescription>
            Ogni incasso confermato riceve un numero del tipo ANNO/0001, in ordine di data d'incasso (a parità di data, di registrazione). Il progressivo riparte a ogni anno.
            Per gli incassi prima del 1° gennaio 2026 il numero è fittizio e la ricevuta lo dichiara: i condomini hanno già quelle rilasciate dalla precedente amministrazione.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button onClick={anteprima} disabled={lavoro}>Anteprima (non modifica nulla)</Button>
          <Button onClick={applica} disabled={lavoro || !piano} variant="secondary">Applica la numerazione</Button>
        </CardContent>
      </Card>

      {fatto && <Card><CardContent className="pt-6 text-sm">Numerazione applicata: {fatto.cambiate} ricevute aggiornate su {fatto.totale}. I vecchi numeri sono salvati.</CardContent></Card>}

      {piano && (
        <Card>
          <CardHeader><CardTitle className="text-base">Anteprima: {piano.totale} incassi, {piano.gia_numerate} già con un numero</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader><TableRow><TableHead>Anno</TableHead><TableHead className="text-right">Ricevute</TableHead><TableHead>Dal</TableHead><TableHead>Al</TableHead><TableHead>Numeri</TableHead><TableHead className="text-right">Cambiano</TableHead></TableRow></TableHeader>
              <TableBody>
                {piano.per_anno.map((a) => (
                  <TableRow key={a.anno}>
                    <TableCell>{a.anno}{Number(a.anno) < 2026 ? ' (numero interno)' : ''}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.ricevute}</TableCell>
                    <TableCell>{dataIt(a.dal)}</TableCell><TableCell>{dataIt(a.al)}</TableCell>
                    <TableCell>{a.primo} → {a.ultimo}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.cambiano}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {piano.esempi_gia_emesse.length > 0 && (
              <div className="text-sm">
                <strong>Ricevute già numerate che cambiano:</strong>
                <ul className="list-disc ml-5">
                  {piano.esempi_gia_emesse.filter((e) => e.vecchio !== e.numero).map((e, i) => <li key={i}>{dataIt(e.data)} · {e.unita}: {e.vecchio} → {e.numero}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RicevuteNumerazione;
